// @flow
import React, { Component } from 'react'
import FieldSet from '../FieldSet'
import styled from 'styled-components'
import _ from 'lodash'
import { makeFbPlacementsConsistent, renderData } from './fbPlacements'
import type { FbPlacements } from '../types'
import LabeledWrapper from './LabeledWrapper'
import type { ConsumerPlatformType } from './fbPlacements'

const PlatformDiv = styled.div`
  margin-bottom: 10px;
`

const PlatformTitleDiv = styled.div`
  font-size: 12px;
  font-weight: bold;
  margin-bottom: 3px;
`

type Props = {|
  value: FbPlacements,
  platform: ConsumerPlatformType,
  audienceType: string,
  errors: Array<string>,
  hide?: boolean,
  onChange: (Object) => void
|}

type State = {|
  isOpen: boolean
|}

const t = {
  select_placements: 'Выбрать Facebook плейсменты',
  open_modal: 'Выбрать плейсменты',
  label: 'Facebook Плейсменты',
  last_position_under_deleting: 'Невозможно удалить последний плейсмент'
}

export default class Placements extends Component<Props, State> {
  constructor(props: Props) {
    super(props)

    this.state = {
      isOpen: false
    }
  }

  render() {
    if (this.props.hide) return null

    return (
      <LabeledWrapper className="t-Placements" label={t.label} errors={this.props.errors} twoLines>
        <FieldSet.Layout>
          {this.renderSelected()}
          <FieldSet.OpenButton label={t.open_modal} onClick={() => this.openModal()} />
          {this.renderModal()}
        </FieldSet.Layout>
      </LabeledWrapper>
    )
  }

  renderSelected() {
    return (
      <FieldSet.Lines>
        {_.map(this.renderData(), (platformData) => {
          const { platform, positions } = platformData
          const selectedPosition = _.filter(positions, 'isSelected')
          if (_.isEmpty(selectedPosition)) return

          return (
            <FieldSet.Line key={platform} title={platformData.title}>
              {selectedPosition.map((positionData) => {
                const { position, label, isDisabled } = positionData

                return (
                  <FieldSet.SelectedInline
                    key={position}
                    node={{ platform, position, label }}
                    isDisabled={isDisabled}
                    onDeselect={(node) => !isDisabled && this.deselectPlacement(node)}
                  />
                )
              })}
            </FieldSet.Line>
          )
        })}
      </FieldSet.Lines>
    )
  }

  renderModal() {
    const { isOpen } = this.state

    // prevent rendering modal when it's closed
    if (!isOpen) return null

    return (
      <FieldSet.Modal
        isOpen={isOpen}
        onClose={() => this.closeModal()}
        header={t.select_placements}
        maxWidth={500}
      >
        <FieldSet.Column fullWidth leftPadding>
          {_.map(this.renderData(), (platformData) => {
            const { platform, title, positions } = platformData

            return (
              <PlatformDiv key={platform}>
                <PlatformTitleDiv>{title}</PlatformTitleDiv>
                {positions.map((positionData) => {
                  const { position, label, isSelected, isDisabled } = positionData

                  return (
                    <FieldSet.SingleNode
                      key={position}
                      label={label}
                      isSelected={isSelected}
                      isDisabled={isDisabled}
                      triggerNodeSelected={(isSelected) =>
                        !isDisabled && this.triggerNodeSelected({ platform, position }, isSelected)
                      }
                    />
                  )
                })}
              </PlatformDiv>
            )
          })}
        </FieldSet.Column>
      </FieldSet.Modal>
    )
  }

  renderData() {
    const { value, audienceType, platform } = this.props

    return renderData(value, audienceType, platform)
  }

  triggerNodeSelected({ platform, position }: Object, isSelected: boolean) {
    const { value } = this.props

    if (isSelected) {
      this.changed({
        ...value,
        [platform]: [...(value[platform] || []), position]
      })
    } else {
      if (this.positionsSelectedCount() === 1) {
        window.alert(t.last_position_under_deleting)
        return
      }

      this.changed({ ...value, [platform]: _.without(value[platform], position) })
    }
  }

  positionsSelectedCount() {
    return _(this.props.value)
      .map((value) => value)
      .flatten()
      .value().length
  }

  deselectPlacement({ platform, position }: Object) {
    if (this.positionsSelectedCount() === 1) {
      window.alert(t.last_position_under_deleting)
      return
    }

    const { value } = this.props

    const chunk = { [platform]: _.without(value[platform], position) }
    this.changed({ ...value, ...chunk })
  }

  changed(fbPlacements: FbPlacements) {
    const { platform, onChange } = this.props

    onChange(makeFbPlacementsConsistent(fbPlacements, platform))
  }

  openModal() {
    this.setState({ isOpen: true })
  }

  closeModal() {
    this.setState({ isOpen: false })
  }
}
